import { Grid } from "@material-ui/core";
import { Sync } from "@material-ui/icons";
import { isValid } from "date-fns";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { convertDateToDisplay } from "../../../helpers/convert-date-to-display";
import { prettifyDate } from "../../../helpers/prettify-date";
import { prettifyTime } from "../../../helpers/prettify-time";
import { HttpClient } from "../../../web-services/http-client";
import { IParkingLotChartData } from "../../../web-services/models/parking-lot-chart-data";
import { objectToFormData } from "../../../web-services/utils/object-to-form-data";
import { DataGetterCard } from "../data-getter-card";
import { ParkingLotChart } from "../parking-lot-chart";
import { ProgressDialog } from "../progress-dialog";
import { setDisplayedParkingPeriod } from "./helpers/set-displayed-parking-period";

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ChartBlock(props: {
    httpClient: HttpClient;
    parkingLotId: number;
    selectedStartDate: Date;
    selectedStartTime: Date | undefined;
    selectedEndDate: Date;
    selectedEndTime: Date | undefined;
    selectedTimeBreakdown: "ora" | "nap" | "nincs" | undefined;
    onTimeBreakdownChange: (val: "ora" | "nap" | "nincs" | undefined) => void;
    handleStartDateChange: (date: any) => void;
    handleStartTimeChange: (date: any) => void;
    handleEndDateChange: (date: any) => void;
    handleEndTimeChange: (date: any) => void;
})
{
    const [chartData, setChartData] = useState<IParkingLotChartData[]>([]);
    const [errorMsg, setErrorMsg] = useState<string>();
    const [errorCategoryMsg, setErrorCategoryMsg] = useState<string>();
    const [progressDialogOpened, setProgressDialogOpened] = useState<boolean>(false);

    useEffect(() =>
    {
        setDisplayedParkingPeriod(props.selectedStartDate, props.selectedEndDate, props.selectedStartTime, props.selectedEndTime);
    },[props.selectedStartTime, props.selectedEndTime]);

    useEffect(() =>
    {
        fetchDataForChart();
    },[]);

    // Only the yyyy.MM.dd HH:mm.ss format needed, nothing more.
    const handleChartData = (data: IParkingLotChartData[]) => setChartData(data.map((d) => ({
        ...d,
        date: convertDateToDisplay(new Date(d.date), props.selectedTimeBreakdown)
    })));

    const fetchDataForChart = () =>
    {
        setProgressDialogOpened(true);
        const fetchData = async () =>
        {
            setErrorMsg(undefined);
            setErrorCategoryMsg(undefined);
            try
            {
                let data = (await props.httpClient.post<IParkingLotChartData[]>(
                    "values/post-chart-data",
                    "",
                    objectToFormData({
                        parkingLotId: props.parkingLotId,
                        startDate: prettifyDate(props.selectedStartDate),
                        endDate: prettifyDate(props.selectedEndDate),
                        startTime: prettifyTime(props.selectedStartTime),
                        endTime: prettifyTime(props.selectedEndTime),
                        timeBreakdown: props.selectedTimeBreakdown
                    }),
                    15000
                )).data;
                handleChartData(data || []);
                setProgressDialogOpened(false);
            }
            catch (err: any)
            {
                console.log(err);

                if (err.message === "Nem várt hiba történt.")
                {
                    setErrorCategoryMsg("Kapcsolati hiba");
                    setErrorMsg("Kérjük, próbálja újra az adatlekérést.");
                }
                else
                {
                    setErrorMsg(err.message);
                }
            }
        };

        fetchData().catch(console.error);
    };

    const { name } = useParams();
    return (
        <>
            <ProgressDialog
                text="Adatbetöltés folyamatban"
                retryButtonText="Újra lekérem"
                onRetryButtonClick={() => fetchDataForChart()}
                onCancelButtonClick={() => setProgressDialogOpened(false)}
                retryButtonIcon={<Sync />}
                errorText={errorMsg}
                errorCategoryText={errorCategoryMsg}
                open={progressDialogOpened}
            />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DataGetterCard
                        startDate={props.selectedStartDate}
                        startTime={props.selectedStartTime}
                        endDate={props.selectedEndDate}
                        endTime={props.selectedEndTime}
                        timeBreakdown={props.selectedTimeBreakdown}
                        onButtonClick={() => fetchDataForChart()}
                        onTimeBreakdownChange={props.onTimeBreakdownChange}
                        onStartDateChange={props.handleStartDateChange}
                        onStartTimeChange={props.handleStartTimeChange}
                        onEndDateChange={props.handleEndDateChange}
                        onEndTimeChange={props.handleEndTimeChange}
                        mode="graphicon"
                        modifiable={false}
                    />
                    <ParkingLotChart
                        data={chartData}
                        parkingZoneTitle={name ? name : ""}
                        parkingPeriod={
                            setDisplayedParkingPeriod(
                                props.selectedStartDate,
                                props.selectedEndDate,
                                props.selectedStartTime,
                                props.selectedEndTime)
                        }
                        timeBreakdown={props.selectedTimeBreakdown}
                    />

                </Grid>
            </Grid>
        </>
    );
}
